import React, { useEffect, useState } from "react";
import api from "../services/api";
import { initAdminPushNotifications } from "../utils/pushNotification";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("last7");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchStats();
    initAdminPushNotifications();
  }, [filter]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      let query = `?filter=${filter}`;
      if (filter === "custom" && startDate && endDate) {
        query += `&startDate=${startDate}&endDate=${endDate}`;
      }
      const res = await api.get(`/dashboard-stats${query}`);
      setStats(res.data.stats);
    } catch (error) {
      console.error("Dashboard Stats Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSearch = (e) => {
    e.preventDefault();
    if (startDate && endDate) fetchStats();
  };

  return (
    <div style={styles.page}>
      {/* HEADER SECTION */}
      <div style={styles.headerContainer}>
        <div>
          <h1 style={styles.title}>System Overview</h1>
          <p style={styles.subtitle}>
            Real-time performance, booking analytics, and user signups
          </p>
        </div>

        {/* CONTROLS & FILTERS */}
        <div style={styles.filterGroup}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.selectInput}
          >
            <option value="today">Today</option>
            <option value="tomorrow">Tomorrow</option>
            <option value="last7">Last 7 Days</option>
            <option value="custom">Custom Range</option>
          </select>

          {filter === "custom" && (
            <form onSubmit={handleCustomSearch} style={styles.customForm}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={styles.dateInput}
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={styles.dateInput}
              />
              <button type="submit" style={styles.applyBtn}>
                Apply
              </button>
            </form>
          )}
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Fetching real-time metrics...</p>
        </div>
      ) : (
        stats && (
          <>
            {/* KPI METRICS GRID */}
            <div style={styles.grid}>
              {/* User Signups */}
              <div style={{ ...styles.card, borderTop: "4px solid #6366f1" }}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardLabel}>User Signups</span>
                  <span style={styles.badgeBlue}>{filter}</span>
                </div>
                <div style={styles.cardValue}>{stats.userSignups}</div>
                <div style={styles.cardSub}>Total registered: {stats.totalUsers}</div>
              </div>

              {/* Driver Signups */}
              <div style={{ ...styles.card, borderTop: "4px solid #3b82f6" }}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardLabel}>Driver Signups</span>
                  <span style={styles.badgeBlue}>{filter}</span>
                </div>
                <div style={styles.cardValue}>{stats.driverSignups}</div>
                <div style={styles.cardSub}>Total fleet: {stats.totalDrivers}</div>
              </div>

              {/* Pending Drivers */}
              <div style={{ ...styles.card, borderTop: "4px solid #f59e0b" }}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardLabel}>Pending Drivers</span>
                  <span style={styles.badgeOrange}>Action Required</span>
                </div>
                <div style={{ ...styles.cardValue, color: "#d97706" }}>
                  {stats.pendingDrivers}
                </div>
                <div style={styles.cardSub}>Awaiting document review</div>
              </div>

              {/* Range Bookings */}
              <div style={{ ...styles.card, borderTop: "4px solid #8b5cf6" }}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardLabel}>Bookings ({filter})</span>
                  <span style={styles.badgePurple}>Rides</span>
                </div>
                <div style={{ ...styles.cardValue, color: "#7c3aed" }}>
                  {stats.totalBookingsInRange}
                </div>
                <div style={styles.cardSub}>Active now: {stats.ongoingBookings}</div>
              </div>
            </div>

            {/* CHARTS GRID */}
            <div style={styles.chartsGrid}>
              {/* LINE / AREA CHART */}
              <div style={styles.chartCard}>
                <div style={styles.chartHeader}>
                  <h3 style={styles.chartTitle}>Booking Volume Over Time</h3>
                  <span style={styles.chartSub}>Trends per day</span>
                </div>
                <div style={styles.chartWrapper}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={stats.timelineData}
                      margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorBooking" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#ffffff",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="Bookings"
                        stroke="#2563eb"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorBooking)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* PIE CHART */}
              <div style={styles.chartCard}>
                <div style={styles.chartHeader}>
                  <h3 style={styles.chartTitle}>Rides Status Breakdown</h3>
                  <span style={styles.chartSub}>Current global state</span>
                </div>
                <div style={styles.chartWrapper}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.bookingStatusBreakdown}
                        cx="50%"
                        cy="45%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={4}
                        dataKey="value"
                      >
                        {stats.bookingStatusBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        wrapperStyle={{ fontSize: "12px", color: "#475569" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* STATUS FOOTER */}
            <div style={styles.footerContainer}>
              <div style={styles.statusBox}>
                <span style={styles.pulseIcon}></span>
                <span>System operational • Live Sync</span>
              </div>
              <span style={styles.lastUpdatedText}>
                Last refreshed: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </>
        )
      )}
    </div>
  );
}

export default Dashboard;

/* ================= STYLES ================= */

const styles = {
  page: {
    padding: "24px",
    background: "#f8fafc",
    minHeight: "100vh",
    boxSizing: "border-box",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "28px",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    margin: "4px 0 0 0",
    color: "#64748b",
    fontSize: "14px",
  },
  filterGroup: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  selectInput: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    fontSize: "13px",
    fontWeight: "600",
    color: "#334155",
    outline: "none",
    cursor: "pointer",
  },
  customForm: {
    display: "flex",
    gap: "6px",
    alignItems: "center",
  },
  dateInput: {
    padding: "6px 10px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "12px",
    outline: "none",
    background: "#ffffff",
  },
  applyBtn: {
    background: "#2563eb",
    color: "#ffffff",
    border: "none",
    padding: "7px 14px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  card: {
    background: "#ffffff",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  cardLabel: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  cardValue: {
    fontSize: "30px",
    fontWeight: "800",
    color: "#0f172a",
    margin: "4px 0",
  },
  cardSub: {
    fontSize: "12px",
    color: "#94a3b8",
    fontWeight: "500",
  },
  badgeBlue: {
    background: "#eff6ff",
    color: "#2563eb",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600",
    textTransform: "capitalize",
  },
  badgeOrange: {
    background: "#fffbebe1",
    color: "#d97706",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600",
  },
  badgePurple: {
    background: "#f3e8ff",
    color: "#7c3aed",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600",
  },
  chartsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
    width: "100%",
  },
  chartCard: {
    background: "#ffffff",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
  },
  chartHeader: {
    marginBottom: "16px",
  },
  chartTitle: {
    margin: 0,
    fontSize: "15px",
    fontWeight: "700",
    color: "#1e293b",
  },
  chartSub: {
    fontSize: "12px",
    color: "#94a3b8",
  },
  chartWrapper: {
    width: "100%",
    height: "250px",
  },
  footerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    background: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    flexWrap: "wrap",
    gap: "10px",
  },
  statusBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#334155",
  },
  pulseIcon: {
    width: "8px",
    height: "8px",
    background: "#10b981",
    borderRadius: "50%",
    boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.2)",
  },
  lastUpdatedText: {
    fontSize: "12px",
    color: "#94a3b8",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "100px 0",
  },
  loadingText: {
    marginTop: "12px",
    fontSize: "14px",
    color: "#64748b",
    fontWeight: "600",
  },
  spinner: {
    width: "32px",
    height: "32px",
    border: "3px solid #e2e8f0",
    borderTop: "3px solid #2563eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};